import { defineStore } from 'pinia';
import type { TesterProject } from '@its/shared';
import { api } from '@/lib/api';

interface State {
  projects: TesterProject[];
  loading: boolean;
  loaded: boolean;
}

/**
 * Espace « Tests et améliorations » — les projets où le collaborateur est
 * étiqueté TESTEUR (0038).
 *
 * Cette liste pilote aussi la VISIBILITÉ de l'entrée de menu : on ne la montre
 * qu'à qui a effectivement quelque chose à y faire. D'où le chargement au
 * niveau de l'application, à la connexion, et non au montage de la page.
 */
export const useTestsStore = defineStore('tests', {
  state: (): State => ({ projects: [], loading: false, loaded: false }),
  getters: {
    /** A-t-on au moins un projet à tester ? Condition d'affichage de l'entrée. */
    isTester: (s): boolean => s.projects.length > 0,
    /**
     * Ce qui ATTEND LE TESTEUR, tous projets confondus — la pastille du menu.
     *
     * C'étaient les cas de test restants ; ce sont désormais les remontées que
     * les développeurs lui ont rendues (« À tester »). Une pastille est un
     * appel à agir : depuis que l'espace ne sert plus à cocher un plan, les cas
     * restants n'appelaient plus rien, et une pastille qui ne redescend jamais
     * cesse d'être lue.
     */
    aRetester: (s): number => s.projects.reduce((n, p) => n + p.remonteesARetester, 0),
  },
  actions: {
    async fetch(): Promise<void> {
      // Squelette au premier chargement seulement ; ensuite mise à jour en place.
      this.loading = !this.loaded;
      try {
        const { projects } = await api.get<{ projects: TesterProject[] }>('/m4/my-test-projects');
        this.projects = projects;
        this.loaded = true;
      } catch {
        // Pas de testeur, pas d'onglet : un échec ici ne doit pas casser la nav.
        this.projects = [];
      } finally {
        this.loading = false;
      }
    },
  },
});
